import { infiniteQueryOptions } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { FilterInput, FilterOperator, OrderDirection, OrderInput } from '#features/api/types';
import { isDefined } from '#utils/isDefined';

const notificationsDoc = graphql(`
  query Notifications($offset: Int, $limit: Int, $orders: [OrderInput], $filters: [FilterInput]) {
    notification {
      messages(offset: $offset, limit: $limit, orders: $orders, filters: $filters) {
        notifications {
          id
          userData {
            displayName
            userId
            avatar
          }
          message
          contentType
          type
          createdAt
          updatedAt
          count
          isRead
        }
        hasMore
        totalCount
      }
    }
  }
`);

export const notificationsQueryOptions = (input: {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
}) => {
  // The messages resolver needs a sort order, including the one-row history check.
  const params = {
    ...input,
    orders: input.orders?.length
      ? input.orders
      : [{ direction: OrderDirection.DESC, field: 'updatedAt' }],
  };
  return {
    queryKey: ['notifications', params],
    queryFn: () =>
      gqlFetcher(notificationsDoc, params).then(
        (data) => data?.notification?.messages?.notifications?.filter(isDefined) ?? []
      ),
  };
};

export type NotificationsTab = 'unread' | 'all';

// Separate key from the compact array query; realtime invalidation of ['notifications']
// still refreshes both surfaces. Keep raw page length for offsets even with null records.
export const notificationHistoryQueryOptions = (tab: NotificationsTab) =>
  infiniteQueryOptions({
    queryKey: ['notifications', 'history', tab],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const data = await gqlFetcher(notificationsDoc, {
        offset: pageParam,
        limit: 20,
        orders: [{ direction: OrderDirection.DESC, field: 'updatedAt' }],
        filters:
          tab === 'unread'
            ? [{ field: 'isRead', operator: FilterOperator.EQUALS, value: false, filters: [] }]
            : undefined,
      });
      const page = data?.notification?.messages;
      if (!page) throw new Error('Missing notification page');
      return {
        notifications: page.notifications?.filter(isDefined) ?? [],
        hasMore: Boolean(page.hasMore),
        nextOffset: pageParam + (page.notifications?.length ?? 0),
      };
    },
    getNextPageParam: (page, _pages, lastOffset) =>
      page.hasMore && page.nextOffset > lastOffset ? page.nextOffset : undefined,
  });

const hasUnreadNotificationsDoc = graphql(`
  query HasUnreadNotifications {
    notification {
      exist
    }
  }
`);

export const hasUnreadNotificationsQueryOptions = () => ({
  queryKey: ['hasUnreadNotifications'],
  queryFn: () =>
    gqlFetcher(hasUnreadNotificationsDoc).then((data) => Boolean(data?.notification?.exist)),
});

const readAllNotificationsDoc = graphql(`
  mutation ReadAllNotifications {
    notification {
      readAll {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const readAllNotifications = () =>
  gqlFetcher(readAllNotificationsDoc).then((data) => data?.notification?.readAll);

const readChatNotificationsDoc = graphql(`
  mutation ReadChatNotifications($parentId: UUID!) {
    notification {
      readChat(parentId: $parentId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const readChatNotifications = (input: { parentId: string }) =>
  gqlFetcher(readChatNotificationsDoc, input).then((data) => data?.notification?.readChat);

const deleteAllNotificationsDoc = graphql(`
  mutation DeleteAllNotifications {
    notification {
      removeAll {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const deleteAllNotifications = () =>
  gqlFetcher(deleteAllNotificationsDoc).then((data) => data?.notification?.removeAll);

const addPushTokenDoc = graphql(`
  mutation AddPushToken($input: PushTokenInput!) {
    push {
      addToken(input: $input) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const addPushToken = (
  input: {
    token: string;
    platform?: string;
    deviceOem?: string;
    callScreenReady?: boolean;
    appVersion?: number;
    // Снимок состояния телефона: едет попутно с регистрацией и ложится в строку токена.
    sdkInt?: number;
    batteryExempt?: boolean;
    bgRestricted?: boolean;
    standbyBucket?: number;
    notificationsEnabled?: boolean;
    fsiAllowed?: boolean;
  },
  credential?: string
) => {
  const request = credential
    ? gqlFetcher(
        addPushTokenDoc,
        { input },
        { headers: { Accesstoken: credential }, nativeTimeoutMs: 10_000 }
      )
    : gqlFetcher(addPushTokenDoc, { input });
  return request.then((data) => data?.push?.addToken);
};

const removePushTokenDoc = graphql(`
  mutation RemovePushToken($input: PushTokenInput!) {
    push {
      removeToken(input: $input) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const removePushToken = (input: { token: string }, credential?: string) => {
  const request = credential
    ? gqlFetcher(
        removePushTokenDoc,
        { input },
        { headers: { Accesstoken: credential }, nativeTimeoutMs: 10_000 }
      )
    : gqlFetcher(removePushTokenDoc, { input });
  return request.then((data) => data?.push?.removeToken);
};
